import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCallStateHooks,
  CallingState,
  ParticipantView,
  useCall,
} from "@stream-io/video-react-sdk";
import RecordDot from "../../assets/svg/RecordDot.svg";
import CallLayout from "./CallLayout";
import CallControls from "./CallControls";
import CustomLocalParticpantView from "./CustomLocalParticpantView";
import Loader from "./Loader";
import ChatBox from "../../pages/Course/Live/Chat/ChatBox";
import { useSelector } from "react-redux";
import { API } from "../../shared/api/Api";
import axios from "axios";
import { selectUserInformations } from "../../features/authSlice";
import ToastNotification from "../Notifications/ToastNotification";
import { useNotificationSoundAndToast } from "../../hooks/useNotificationSoundAndToast";
import { AnimatePresence } from "framer-motion";
import Close from "../../assets/svg/CloseButton.svg";
import { useReactMediaRecorder } from "react-media-recorder";
import { showToast } from "../../utils/utils";

const LiveRoom = () => {
  const [screenSharingParticipant, setScreenSharingParticipant] =
    useState<any>(null);
  const [isSlideOpen, setIsSlideOpen] = useState<boolean>(false);
  const [loader, setLoader] = useState(false);
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);

  const navigate = useNavigate();
  const params = useParams();
  const call = useCall();
  const {
    useCallCallingState,
    useLocalParticipant,
    useScreenShareState,
    useCallSession,
    useParticipants,
  } = useCallStateHooks();
  const session = useCallSession();
  const { toastData, setToastData } = useNotificationSoundAndToast();

  const callingState = useCallCallingState();
  const localParticipant = useLocalParticipant();
  const screenShareState = useScreenShareState();
  const participants = useParticipants();
  const isScreenSharing = screenShareState.status === "enabled";
  const user = useSelector(selectUserInformations);
  const { width, height } = window.screen;
  const callCidRef = useRef<string | null>(null);

  const {
    status: recordingStatus,
    startRecording,
    stopRecording,
  } = useReactMediaRecorder({
    mediaRecorderOptions: {
      mimeType: "video/mp4",
    },
    screen: true,
    video: {
      width: { ideal: width },
      height: { ideal: height },
    },
    audio: {
      autoGainControl: false,
      echoCancellation: false,
      noiseSuppression: false,
      sampleSize: 16,
      // @ts-ignore - Experimental chrome property
      suppressLocalAudioPlayback: true,
    },
    onStop: async (blobUrl, blob) => {
      if (blob && blob.size > 0) {
        const filename = `recording-${Date.now()}.mp4`;
        const file = new File([blob], filename, { type: blob.type });

        try {
          await uploadRecording(file, callCidRef.current);
          console.log("Recording uploaded successfully");
        } catch (error) {
          console.error("Failed to upload recording:", error);
        }
      } else {
        console.error("No recording data available");
      }
    },
  });

  const handleRecordingStopped = useCallback(async () => {
    try {
      if (recordingStatus === "recording") {
        stopRecording();
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      // await call?.leave();
      // navigate("/dashboard/courses");
    } catch (error) {
      console.error("Call end error:", error);
      showToast("Error", "Failed to clean up call resources", "error");
    }
  }, [call, navigate, recordingStatus, stopRecording]);

  const handleParticipantLeft = (event: any) => {
    if (event.participant.userId === user.id) return;
    showToast(
      "Participant Left",
      `${event.participant.userId} left the call`,
      "info"
    );
  };

  useEffect(() => {
    if (!call) return;

    callCidRef.current = call.cid;

    const handleHostForceEnd = () => {
      showToast("Session Ended", "Host terminated the call", "info");
      handleRecordingStopped();
    };

    call.on("call.ended", handleHostForceEnd);
    call.on("call.session_participant_left", handleParticipantLeft);

    return () => {
      call.off("call.ended", handleHostForceEnd);
      call.off("call.session_participant_left", handleParticipantLeft);
    };
  }, [call, user.id, handleRecordingStopped]);

  // useEffect(() => {
  //   if (screenShareState.status === "disabled") {
  //     setScreenSharingParticipant(null);
  //     setIsSlideOpen(false);
  //   }
  // }, [screenShareState.status]);

  // Handle recording timer
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (session?.started_at) {
      // Convert database timestamp to milliseconds
      const startTime = Date.parse(
        // @ts-ignore - Experimental chrome property
        session?.started_at
      );

      // Update immediately first
      setRecordingTime(Math.floor((Date.now() - startTime) / 1000));

      // Then update every second
      intervalId = setInterval(() => {
        const elapsedTime = Math.floor((Date.now() - startTime) / 1000);
        setRecordingTime(elapsedTime);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [session?.started_at]); // Run when started_at changes

  // Update recording status state
  useEffect(() => {
    setIsRecordingStarted(recordingStatus === "recording");
  }, [recordingStatus]);

  useEffect(() => {
    const initializeCall = async () => {
      try {
        setLoader(true);
        if (
          call?.state.callingState === CallingState.JOINED &&
          user.isInstructor
        ) {
          startRecording();
        }
      } catch (error) {
        console.error("Call initialization error:", error);
      } finally {
        setLoader(false);
      }
    };

    initializeCall();
  }, [call]);

  const uploadRecording = async (file: File, call_cid: string | null) => {
    try {
      const formData = new FormData();
      formData.append("call_cid", call_cid || "");
      formData.append("file", file, file.name);

      const response = await axios.post(
        `${API.Webhooks.download_files_from_stream_io}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.status === 200) {
        console.log(response);
        console.log("Upload successful");
        return true;
      }
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  };

  useEffect(() => {
    const handleDisableMedia = async () => {
      if (call) {
        await call.camera.disable();
        await call.microphone.disable();
        await call.screenShare.disable();
      }
    };

    return () => {
      handleDisableMedia();
    };
  }, [call]);

  const formatTime = useCallback((timeInSeconds: number) => {
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = timeInSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }, []);

  if (loader) return <Loader />;

  return (
    <div className="relative flex flex-1 justify-center items-center m-auto w-full h-screen">
      {/* Desktop Layout */}
      <div className="hidden sl:block w-full h-full">
        {/* ChatBox */}
        <div className="absolute bg-white rounded-2xl sl:w-1/3 ll:w-1/4 sl:h-[50vh] ll:h-[60vh] top-[10vh] sl:left-[2%] ll:left-[5%] shadow-md">
          <ChatBox room_uuid={params.id} />
        </div>

        {/* Notification Toast */}
        <AnimatePresence>
          {toastData && (
            <ToastNotification
              message={toastData.message}
              image={toastData.image}
              onClose={() => setToastData(null)}
            />
          )}
        </AnimatePresence>

        {/* Video Layout */}
        <div className="absolute w-3/5 sl:h-[50vh] ll:h-[60vh] top-[10vh] sl:left-[40%] ll:left-[35%] rounded-lg shadow-md bg-white overflow-hidden">
          <CallLayout onScreenShare={setScreenSharingParticipant} />
        </div>

        {/* Session Details */}
        <div
          className={`absolute ${
            isSlideOpen ? "w-[35vw]" : "w-[30vw]"
          } bg-gray-200 sl:h-[10vh] ll:h-[12vh] top-[75vh] sl:left-[3%] ll:left-[5%] transition-all duration-300 rounded-lg shadow-sm`}
        >
          <Details
            screenSharingParticipant={screenSharingParticipant}
            isSlideOpen={isSlideOpen}
            setOpen={setIsSlideOpen}
            formatTime={formatTime}
            recordingTime={recordingTime}
            roomUUID={params.id}
          />
        </div>

        {/* Controls */}
        <div
          className={`absolute bg-white rounded-lg shadow-md ${
            isSlideOpen
              ? "sl:left-[60%] ll:left-[60%]"
              : "sl:left-[43%] ll:left-[40%]"
          } sl:h-[10vh] ll:h-[12vh] top-[75vh] sl:w-[35%] ll:w-[40%] flex justify-center items-center p-0 transition-all duration-300`}
        >
          <CallControls
            displayButtons={isSlideOpen ? 3 : 0}
            handleRecordingStopped={handleRecordingStopped}
          />
        </div>

        {/* Local Participant View */}
        <div className="absolute bg-white sl:w-[12.5rem] ll:w-[13.375rem] h-[12vh] top-[75vh] sl:left-[78.5%] ll:left-[83%] rounded-lg shadow-md overflow-hidden">
          {localParticipant && (
            <ParticipantView
              participant={localParticipant}
              ParticipantViewUI={CustomLocalParticpantView}
            />
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden w-full h-full flex flex-col p-4 gap-y-4 relative">
        {/* Top Bar */}
        <div className="w-full flex justify-between items-start z-10 sticky top-16 bg-gray-100 pt-4 pb-2">
          <button
            className="bg-blue-500 text-white rounded-lg px-4 py-2 shadow-lg ml-2"
            onClick={() => setIsChatOpen(true)}
          >
            Open Chat
          </button>
          <div className="w-24 h-18 rounded-lg overflow-hidden bg-white mr-2">
            {localParticipant && (
              <ParticipantView
                participant={localParticipant}
                ParticipantViewUI={CustomLocalParticpantView}
              />
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto space-y-4">
          <div className="relative rounded-lg shadow-md bg-white aspect-video">
            <CallLayout onScreenShare={setScreenSharingParticipant} />
          </div>

          <div className="w-full bg-white rounded-lg shadow-md p-4">
            <Details
              screenSharingParticipant={screenSharingParticipant}
              isSlideOpen={isSlideOpen}
              setOpen={setIsSlideOpen}
              formatTime={formatTime}
              recordingTime={recordingTime}
              roomUUID={params.id}
            />
          </div>

          <div className="w-full bg-white rounded-lg shadow-md p-4 mb-4">
            <CallControls
              displayButtons={isSlideOpen ? 3 : 0}
              handleRecordingStopped={handleRecordingStopped}
            />
          </div>
        </div>

        {/* Mobile Chat Modal */}
        {isChatOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
            <div className="absolute bottom-0 left-0 right-0 h-[70vh] bg-white rounded-t-2xl p-6">
              <button
                className="absolute top-2 right-2 text-gray-500"
                onClick={() => setIsChatOpen(false)}
              >
                <img
                  className="w-[20px] h-[20px] rounded-lg"
                  src={Close}
                  alt="close"
                />
              </button>
              <ChatBox room_uuid={params.id} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveRoom;

// const Details = ({
//   screenSharingParticipant,
//   isSlideOpen,
//   setOpen,
//   formatTime,
//   recordingTime,
//   roomUUID,
// }: {
//   screenSharingParticipant: any;
//   isSlideOpen: boolean;
//   setOpen: any;
//   formatTime: (time: number) => string;
//   recordingTime: number;
//   roomUUID: string | undefined;
// }) => {
//   const [liveDetails, setLiveDetails] = useState<any>();
//   const [errorLiveDetails, setErrorLiveDetails] = useState<boolean>();
//   const { useScreenShareState } = useCallStateHooks();
//   const screenShareState = useScreenShareState();
//   const user = useSelector(selectUserInformations);
//   const trimNumber = 15;

//   // Extract screen sharing details dynamically
//   const isScreenSharing = screenShareState.status === "enabled";

//   useEffect(() => {
//     if (screenShareState.status == "disabled") setOpen(false);
//   }, [screenShareState, isSlideOpen]);

//   useEffect(() => {
//     const fetchLesson = async () => {
//       try {
//         const { data } = await axios.get(
//           `${API.Courses.app_courses_lessons_getLessonByRoomUUID}/${roomUUID}`,
//           {
//             headers: {
//               Authorization: `Bearer ${user.token}`,
//             },
//           }
//         );
//         if (data.status === 200 && data.errorCode === 0) {
//           setLiveDetails(data.data);
//           setErrorLiveDetails(false);
//         }
//       } catch (error) {
//         setErrorLiveDetails(true);
//         console.log(error);
//       }
//     };

//     fetchLesson();
//   }, []);

//   return (
//     <div className="flex flex-col lg:flex-row justify-between items-center w-full h-full bg-white p-4 md:p-6 rounded-lg shadow-md relative">
//       {/* Left Section: Course Details */}
//       {liveDetails && (
//         <div
//           className={`transition-all duration-300 w-full lg:w-1/2 xl:w-2/3 flex flex-col items-center lg:items-start text-center lg:text-left mb-4 lg:mb-0`}
//         >
//           <h2 className="text-[20px] md:text-[24px] lg:text-[28px] xl:text-[30px] font-medium text-[#134F98] mb-1 md:mb-2">
//             {/* {trimString(liveDetails?.courseTitle, trimNumber)} */}
//             {liveDetails?.courseTitle}
//           </h2>
//           <p className="text-[14px] md:text-[16px] lg:text-[18px] text-[#929292]">
//             {/* {trimString(liveDetails?.instrctor_name, trimNumber)} */}
//             {liveDetails?.instrctor_name}
//           </p>
//         </div>
//       )}

//       {/* Center Section: Recording Time */}
//       {!isScreenSharing && (
//         <div className="flex flex-col items-center gap-1 md:gap-2 mb-4 lg:mb-0 lg:mr-4 xl:mr-8">
//           <div className="flex items-center text-[#134F98] text-[20px] md:text-[24px] lg:text-[28px] font-medium">
//             <img
//               className="w-4 h-4 md:w-5 md:h-5 mr-2"
//               src={RecordDot}
//               alt="record"
//             />
//             <span>{formatTime(recordingTime)}</span>
//           </div>
//           {liveDetails && (
//             <p className="text-[16px] md:text-[18px] lg:text-[20px] text-gray-600 text-center">
//               {/* {trimString(liveDetails?.title, trimNumber)} */}
//               {liveDetails?.title}
//             </p>
//           )}
//         </div>
//       )}

//       {/* Right Section: Slide Content */}
//       {isSlideOpen && (
//         <div className="hidden lg:flex flex-col items-center gap-2 w-full lg:w-auto text-center mb-4 lg:mb-0">
//           <div className="flex items-center text-[#134F98] text-[20px] md:text-[24px] lg:text-[28px] font-medium">
//             <img
//               className="w-4 h-4 md:w-5 md:h-5 mr-2"
//               src={RecordDot}
//               alt="record"
//             />
//             <span>{formatTime(recordingTime)}</span>
//           </div>
//           {liveDetails && (
//             <p className="text-[16px] md:text-[18px] lg:text-[20px] text-gray-600">
//               {liveDetails?.title}
//               {/* {trimString(liveDetails?.title, trimNumber)} */}
//             </p>
//           )}
//         </div>
//       )}

//       {/* Mobile Toggle Button */}
//       {isScreenSharing && screenSharingParticipant && (
//         <button
//           onClick={() => setOpen(!isSlideOpen)}
//           className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full w-8 h-8 flex items-center justify-center z-50 shadow-sm"
//         >
//           {isSlideOpen ? (
//             <img src={ArrowHeadLeft} className="w-4 h-4" alt="<" />
//           ) : (
//             <img src={ArrowHeadRight} className="w-4 h-4" alt=">" />
//           )}
//         </button>
//       )}

//       {/* Desktop Toggle Button */}
//       {isScreenSharing && screenSharingParticipant && (
//         <button
//           onClick={() => setOpen(!isSlideOpen)}
//           className={`hidden lg:flex absolute ${
//             isSlideOpen ? "right-[calc(100%+8px)]" : "right-4"
//           } top-1/2 -translate-y-1/2 text-white rounded-full w-8 h-8 items-center justify-center z-50 bg-blue-500 hover:bg-blue-600 transition-colors`}
//         >
//           {isSlideOpen ? (
//             <img src={ArrowHeadLeft} className="w-4 h-4" alt="<" />
//           ) : (
//             <img src={ArrowHeadRight} className="w-4 h-4" alt=">" />
//           )}
//         </button>
//       )}

//       {/* Screen Sharing Participant */}
//       {isScreenSharing && screenSharingParticipant && (
//         <div
//           className={`lg:absolute lg:right-4 lg:bottom-4 w-full lg:w-[200px] mt-4 lg:mt-0 ${
//             isSlideOpen ? "lg:translate-x-[-240px]" : ""
//           } transition-transform duration-300`}
//         >
//           <div className="relative aspect-video lg:w-[200px] bg-gray-100 rounded-lg overflow-hidden">
//             <ParticipantView
//               participant={screenSharingParticipant}
//               trackType="videoTrack"
//               ParticipantViewUI={CustomCallOwner}
//             />
//           </div>
//         </div>
//       )}
//     </div>
//   );
// };

const Details = ({
  screenSharingParticipant,
  isSlideOpen,
  setOpen,
  formatTime,
  recordingTime,
  roomUUID,
}: {
  screenSharingParticipant: any;
  isSlideOpen: boolean;
  setOpen: any;
  formatTime: (time: number) => string;
  recordingTime: number;
  roomUUID: string | undefined;
}) => {
  const [liveDetails, setLiveDetails] = useState<any>();
  const [errorLiveDetails, setErrorLiveDetails] = useState<boolean>();
  const { useScreenShareState } = useCallStateHooks();
  const screenShareState = useScreenShareState();
  const user = useSelector(selectUserInformations);
  const trimNumber = 15;

  // Extract screen sharing details dynamically
  const isScreenSharing = screenShareState.status === "enabled";

  useEffect(() => {
    if (screenShareState.status == "disabled") setOpen(false);
  }, [screenShareState, isSlideOpen]);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const { data } = await axios.get(
          `${API.Courses.app_courses_lessons_getLessonByRoomUUID}/${roomUUID}`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        if (data.status === 200 && data.errorCode === 0) {
          setLiveDetails(data.data);
          setErrorLiveDetails(false);
        }
      } catch (error) {
        setErrorLiveDetails(true);
        console.log(error);
      }
    };

    fetchLesson();
  }, []);

  return (
    <div className="flex flex-col sl:flex-row justify-between items-center w-full h-full bg-white p-4 sl:p-6 rounded-lg shadow-md relative">
      {/* Left Section: Course Details */}
      {liveDetails && (
        <div className="transition-all duration-300 w-full sl:w-1/2 ll:w-2/3 flex flex-col items-center sl:items-start text-center sl:text-left mb-4 sl:mb-0">
          <h2 className="text-md sl:text-lg ll:text-[1.75rem] font-medium text-primary mb-1 sl:mb-2">
            {liveDetails?.courseTitle}
          </h2>
          <p className="text-sm sl:text-md ll:text-lg text-[#929292]">
            {liveDetails?.instrctor_name}
          </p>
        </div>
      )}

      {/* Center Section: Recording Time */}
      {/* {!isScreenSharing && ( */}
      <div className="flex flex-col items-center gap-1 sl:gap-2 mb-4 sl:mb-0 sl:mr-4 ll:mr-8">
        <div className="flex items-center text-primary text-md sl:text-lg ll:text-[1.75rem] font-medium">
          <img
            className="w-4 h-4 sl:w-5 sl:h-5 mr-2"
            src={RecordDot}
            alt="record"
          />
          <span>{formatTime(recordingTime)}</span>
        </div>
        {liveDetails && (
          <p className="text-base sl:text-lg ll:text-xl text-gray-600 text-center">
            {liveDetails?.title}
          </p>
        )}
      </div>
      {/* )} */}

      {/* Right Section: Slide Content */}
      {isSlideOpen && (
        <div className="hidden sl:flex flex-col items-center gap-2 w-full sl:w-auto text-center mb-4 sl:mb-0">
          <div className="flex items-center text-primary text-md sl:text-lg ll:text-[1.75rem] font-medium">
            <img
              className="w-4 h-4 sl:w-5 sl:h-5 mr-2"
              src={RecordDot}
              alt="record"
            />
            <span>{formatTime(recordingTime)}</span>
          </div>
          {liveDetails && (
            <p className="text-base sl:text-lg ll:text-xl text-gray-600">
              {liveDetails?.title}
            </p>
          )}
        </div>
      )}

      {/* Mobile Toggle Button */}
      {/* {isScreenSharing && screenSharingParticipant && (
        <button
          onClick={() => setOpen(!isSlideOpen)}
          className="sl:hidden absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 rounded-full w-8 h-8 flex items-center justify-center z-50 shadow-sm"
        >
          {isSlideOpen ? (
            <img src={ArrowHeadLeft} className="w-4 h-4" alt="<" />
          ) : (
            <img src={ArrowHeadRight} className="w-4 h-4" alt=">" />
          )}
        </button>
      )} */}

      {/* Desktop Toggle Button */}
      {/* {isScreenSharing && screenSharingParticipant && (
        <button
          onClick={() => setOpen(!isSlideOpen)}
          className={`hidden sl:flex absolute ${
            isSlideOpen ? "right-[calc(100%+0.5rem)]" : "right-4"
          } top-1/2 -translate-y-1/2 text-white rounded-full w-8 h-8 items-center justify-center z-50 bg-blue-500 hover:bg-blue-600 transition-colors`}
        >
          {isSlideOpen ? (
            <img src={ArrowHeadLeft} className="w-4 h-4" alt="<" />
          ) : (
            <img src={ArrowHeadRight} className="w-4 h-4" alt=">" />
          )}
        </button>
      )} */}

      {/* Screen Sharing Participant */}
      {/* {isScreenSharing && screenSharingParticipant && (
        <div
          className={`sl:absolute sl:right-4 sl:bottom-4 w-full sl:w-[12.5rem] mt-4 sl:mt-0 ${
            isSlideOpen ? "sl:translate-x-[-15rem]" : ""
          } transition-transform duration-300`}
        >
          <div className="relative aspect-video sl:w-[12.5rem] bg-gray-100 rounded-lg overflow-hidden">
            <ParticipantView
              participant={screenSharingParticipant}
              trackType="videoTrack"
              ParticipantViewUI={CustomCallOwner}
            />
          </div>
        </div>
      )} */}
    </div>
  );
};
